/**
 * EssentialCheckEditor - 필수 체크 편집 모달
 *
 * 최대 3개의 {answer, desc} 항목을 편집.
 */
import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2, Save, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { quetureContentApi } from '@/api/quetureContent'
import type { EssentialCheckItem } from '@/api/quetureContent'

const EMPTY_ITEM = (n: number): EssentialCheckItem => ({ number: n, answer: null, desc: null })

interface Props {
  quetureId: string
  onClose: () => void
}

export function EssentialCheckEditor({ quetureId, onClose }: Props) {
  const queryClient = useQueryClient()
  const [items, setItems] = useState<EssentialCheckItem[]>([EMPTY_ITEM(1), EMPTY_ITEM(2), EMPTY_ITEM(3)])
  const [loaded, setLoaded] = useState(false)

  const { data: fetchedData, isLoading } = useQuery({
    queryKey: ['essential-check', quetureId],
    queryFn: () => quetureContentApi.getEssentialCheck(quetureId),
  })

  useEffect(() => {
    if (fetchedData && !loaded) {
      const padded: EssentialCheckItem[] = [
        fetchedData[0] ?? EMPTY_ITEM(1),
        fetchedData[1] ?? EMPTY_ITEM(2),
        fetchedData[2] ?? EMPTY_ITEM(3),
      ]
      setItems(padded)
      setLoaded(true)
    }
  }, [fetchedData, loaded])

  const saveMutation = useMutation({
    mutationFn: () => {
      // Filter empties FIRST, then renumber sequentially so numbers are always 1..N.
      // (Matches backend saveEssentialCheck behavior — commit e33ff42f.)
      const cleaned: EssentialCheckItem[] = items
        .map((it) => ({
          answer: it.answer?.trim() || null,
          desc: it.desc?.trim() || null,
        }))
        .filter((it) => it.answer || it.desc)
        .map((it, idx) => ({
          number: idx + 1,
          answer: it.answer,
          desc: it.desc,
        }))
      return quetureContentApi.saveEssentialCheck(quetureId, cleaned)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      queryClient.invalidateQueries({ queryKey: ['inspection', 'quetureContent', quetureId] })
      toast.success('필수 체크 저장 완료')
      onClose()
    },
    onError: () => toast.error('저장 실패'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => quetureContentApi.deleteEssentialCheck(quetureId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queture-grades'] })
      queryClient.invalidateQueries({ queryKey: ['inspection', 'quetureContent', quetureId] })
      toast.success('필수 체크 삭제 완료')
      onClose()
    },
    onError: () => toast.error('삭제 실패'),
  })

  const updateField = (index: number, field: 'answer' | 'desc', value: string) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: value || null } : it)))
  }

  if (isLoading || !loaded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl p-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto" />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-bold text-gray-900">필수 체크 설정</h2>
            <p className="text-sm text-gray-500 font-mono">{quetureId}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.map((item, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50/50">
              <span className="text-sm font-semibold text-teal-700">필수 체크 {index + 1}</span>

              <div>
                <label className="text-xs font-medium text-gray-600">정답/모범답안</label>
                <input
                  type="text"
                  value={item.answer ?? ''}
                  onChange={(e) => updateField(index, 'answer', e.target.value)}
                  placeholder={`예: 필수 체크 ${index + 1}`}
                  className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600">해설</label>
                <textarea
                  value={item.desc ?? ''}
                  onChange={(e) => updateField(index, 'desc', e.target.value)}
                  rows={2}
                  placeholder="필수 체크 해설을 입력하세요"
                  className="w-full mt-1 px-2 py-1.5 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={() => {
              if (confirm('필수 체크를 삭제하시겠습니까?')) deleteMutation.mutate()
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
            삭제
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              취소
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              저장
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
